import { formatOrganisationLocationsSummary } from '@/lib/organisation-locations'
import { formatEntityLocation } from '@/utils/format-entity-location'

export const COMPANY_SIZE_OPTIONS = [
  { value: '1_10', label: '1–10 employees' },
  { value: '11_50', label: '11–50 employees' },
  { value: '51_200', label: '51–200 employees' },
  { value: '201_500', label: '201–500 employees' },
  { value: '501_1000', label: '501–1,000 employees' },
  { value: '1001_5000', label: '1,001–5,000 employees' },
  { value: '5001_10000', label: '5,001–10,000 employees' },
  { value: '10001_plus', label: '10,001+ employees' },
] as const

export type CompanySizeValue = (typeof COMPANY_SIZE_OPTIONS)[number]['value']

export const COMPANY_SIZE_LABELS: Record<CompanySizeValue, string> = Object.fromEntries(
  COMPANY_SIZE_OPTIONS.map((option) => [option.value, option.label]),
) as Record<CompanySizeValue, string>

export const FOUNDED_YEAR_MIN = 1800

export function currentFoundedYearMax(): number {
  return new Date().getFullYear()
}

export function formatCompanySizeLabel(value?: string | null): string | null {
  const normalized = value?.trim() ?? ''
  if (!normalized) return null
  return COMPANY_SIZE_LABELS[normalized as CompanySizeValue] ?? null
}

export function formatFoundedYearLabel(year?: number | null): string | null {
  if (year == null || Number.isNaN(year)) return null
  return `Founded ${year}`
}

/** Draft/input string → API year or null; returns validation message when invalid. */
export function parseFoundedYearForSave(raw: string | undefined): {
  founded_year: number | null
  error: string | null
} {
  const trimmed = (raw ?? '').trim()
  if (!trimmed) {
    return { founded_year: null, error: null }
  }
  const year = Number.parseInt(trimmed, 10)
  if (!Number.isFinite(year) || String(year) !== trimmed) {
    return { founded_year: null, error: 'Founded year must be a whole number' }
  }
  if (year < FOUNDED_YEAR_MIN || year > currentFoundedYearMax()) {
    return {
      founded_year: null,
      error: `Founded year must be between ${FOUNDED_YEAR_MIN} and ${currentFoundedYearMax()}`,
    }
  }
  return { founded_year: year, error: null }
}

export function foundedYearDraftValue(year?: number | null): string {
  return year != null && !Number.isNaN(year) ? String(year) : ''
}

export type OrganisationFactsInput = {
  company_size?: string | null
  founded_year?: number | null
  city?: string | null
  country?: string | null
  locations?: Array<{ city?: string | null; country?: string | null; description?: string | null }>
}

export type OrganisationFactKind = 'company_size' | 'founded_year' | 'location'

export type OrganisationFactItem = {
  kind: OrganisationFactKind
  label: string
}

/** Icon-labelled fact items for org identity read surfaces (size, founded, location). */
export function buildOrganisationFactItems(input: OrganisationFactsInput): OrganisationFactItem[] {
  const items: OrganisationFactItem[] = []
  const sizeLabel = formatCompanySizeLabel(input.company_size)
  if (sizeLabel) items.push({ kind: 'company_size', label: sizeLabel })
  const foundedLabel = formatFoundedYearLabel(input.founded_year ?? null)
  if (foundedLabel) items.push({ kind: 'founded_year', label: foundedLabel })
  const location =
    formatOrganisationLocationsSummary(input.locations) || formatEntityLocation(input.city, input.country)
  if (location) items.push({ kind: 'location', label: location })
  return items
}

/** Segments for the org secondary facts line (size · founded · location). */
export function buildOrganisationFactsSegments(input: OrganisationFactsInput): string[] {
  return buildOrganisationFactItems(input).map((item) => item.label)
}

export function formatOrganisationFactsLine(input: OrganisationFactsInput): string | null {
  const segments = buildOrganisationFactsSegments(input)
  return segments.length > 0 ? segments.join(' · ') : null
}
